import React, { useEffect, useMemo, useState } from "react";
import { Tooltip, Select, MenuItem, styled } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { enqueueSnackbar } from "notistack";
import MergerTableColumns from "./MergerTableColumns";
import SearchDatasetCards from "./SearchDatasetCards";
import { Dataset } from "../hooks/useMergeHandler";
import colors from "assets/theme-dark/base/colors";
import pxToRem from "assets/theme/functions/pxToRem";
import Divider from "@mui/material/Divider";

interface CardsSectionProps {
  datasets: Dataset[];
  openedDatasetIds: string[];
  handleCardClick: (datasetId: string) => void;
  selectedColumns: Record<
    string,
    { selectedColumns: string[]; joinColumn: string | null; joinWith?: string; joinWithColumn?: string | null }
  >;
  setSelectedColumns: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        { selectedColumns: string[]; joinColumn: string | null; joinWith?: string; joinWithColumn?: string | null }
      >
    >
  >;
  datasetColumns: Record<string, string[]>;
  isLoading: boolean;
  tableOrder: string[];
  isReplacingView: boolean;
}

const CardsSection: React.FC<CardsSectionProps> = ({
  datasets,
  openedDatasetIds,
  handleCardClick,
  selectedColumns,
  setSelectedColumns,
  datasetColumns,
  tableOrder,
  isLoading,
  isReplacingView,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredDatasets = datasets.filter((dataset) => dataset.name.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleColumnSelect = (datasetId: string, columns: string[], joinColumn: string | null) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [datasetId]: {
        ...prev[datasetId],
        selectedColumns: columns,
        joinColumn,
      },
    }));
  };

  const handleJoinWithChange = (datasetId: string, joinWith: string) => {
    const joinWithDataset = selectedColumns[joinWith];
    const joinWithColumn = joinWithDataset?.joinColumn || null;
    const currentColumns = datasetColumns[datasetId] || [];
    const joinColumnOptions = currentColumns.filter(
      (column) =>
        column !== "FX_UNIQUE_ID" &&
        column !== "CRUXY_ID" &&
        !column.includes("SCORE_") &&
        column !== "WASALREADYMERGEDFROMDATASETS"
    );
    const newJoinColumn =
      joinWithColumn && joinColumnOptions.includes(joinWithColumn) ? joinWithColumn : joinColumnOptions[0] || null;

    setSelectedColumns((prev) => ({
      ...prev,
      [datasetId]: {
        ...prev[datasetId],
        joinWith,
        joinColumn: newJoinColumn,
        joinWithColumn: null,
        selectedColumns: prev[datasetId]?.selectedColumns || [],
      },
    }));
  };

  const handleJoinWithColumnChange = (datasetId: string, joinWithColumn: string | null) => {
    const currentColumns = datasetColumns[datasetId] || [];
    const joinColumnOptions = currentColumns.filter(
      (column) =>
        column !== "FX_UNIQUE_ID" &&
        column !== "CRUXY_ID" &&
        !column.includes("SCORE_") &&
        column !== "WASALREADYMERGEDFROMDATASETS"
    );
    const newJoinColumn =
      joinWithColumn && joinColumnOptions.includes(joinWithColumn) ? joinWithColumn : joinColumnOptions[0] || null;

    setSelectedColumns((prev) => {
      const updated = { ...prev };
      updated[datasetId] = {
        ...prev[datasetId],
        joinColumn: newJoinColumn,
        joinWithColumn,
        selectedColumns: prev[datasetId]?.selectedColumns || [],
      };

      if (isReplacingView && joinWithColumn && prev[datasetId]?.joinWith) {
        const joinWithDatasetId = prev[datasetId].joinWith;
        if (joinWithDatasetId && !prev[joinWithDatasetId]?.selectedColumns.includes(joinWithColumn)) {
          updated[joinWithDatasetId] = {
            ...prev[joinWithDatasetId],
            selectedColumns: [...(prev[joinWithDatasetId]?.selectedColumns || []), joinWithColumn],
          };
        }
      }

      return updated;
    });
  };

  useEffect(() => {
    if (isReplacingView) {
      setSelectedColumns((prev) => {
        const updated = { ...prev };
        const requiredJoinColumns: Record<string, Set<string>> = {};

        Object.keys(prev).forEach((datasetId) => {
          requiredJoinColumns[datasetId] = new Set();
        });

        Object.entries(prev).forEach(([datasetId, config]) => {
          if (config.joinWith && config.joinWithColumn) {
            const joinWithIndex = tableOrder.indexOf(config.joinWith);
            const datasetIndex = tableOrder.indexOf(datasetId);
            if (joinWithIndex !== -1 && joinWithIndex < datasetIndex) {
              requiredJoinColumns[config.joinWith].add(config.joinWithColumn);
            }
          }
        });

        Object.entries(prev).forEach(([datasetId, config]) => {
          const currentSelected = new Set(config.selectedColumns);
          const requiredColumns = requiredJoinColumns[datasetId];

          const newSelectedColumns = Array.from(currentSelected).filter((col) => requiredColumns.has(col));

          requiredColumns.forEach((col) => {
            if (!newSelectedColumns.includes(col)) {
              newSelectedColumns.push(col);
            }
          });

          updated[datasetId] = {
            ...config,
            selectedColumns: newSelectedColumns,
          };
        });

        return updated;
      });
    }
  }, [isReplacingView, setSelectedColumns, tableOrder]);

  useEffect(() => {
    const updatedSelectedColumns = { ...selectedColumns };
    let hasChanges = false;

    const joinReferences: Record<string, { datasetId: string; joinWithColumn: string | null }[]> = {};
    Object.keys(selectedColumns).forEach((datasetId) => {
      joinReferences[datasetId] = [];
    });
    Object.entries(selectedColumns).forEach(([datasetId, config]) => {
      if (config.joinWith && config.joinWithColumn) {
        joinReferences[config.joinWith]?.push({ datasetId, joinWithColumn: config.joinWithColumn });
      }
    });

    tableOrder?.forEach((datasetId, idx) => {
      const isFirstTable = idx === 0;
      if (!isFirstTable && updatedSelectedColumns[datasetId]?.joinWith) {
        const joinWith = updatedSelectedColumns[datasetId].joinWith;
        const joinWithIndex = tableOrder.indexOf(joinWith);
        if (joinWithIndex > idx || joinWithIndex === -1) {
          const previousJoinWith = updatedSelectedColumns[datasetId].joinWith;
          const previousJoinWithColumn = updatedSelectedColumns[datasetId].joinWithColumn;
          const validJoinWith = tableOrder.slice(0, idx).find((id) => tableOrder.includes(id)) || "";
          updatedSelectedColumns[datasetId] = {
            ...updatedSelectedColumns[datasetId],
            joinWith: validJoinWith,
            joinWithColumn: null,
            joinColumn:
              datasetColumns[datasetId]?.filter(
                (col) =>
                  !["FX_UNIQUE_ID", "CRUXY_ID", "SCORE_", "WASALREADYMERGEDFROMDATASETS"].some((exclude) =>
                    col.includes(exclude)
                  )
              )[0] || null,
          };
          hasChanges = true;

          if (isReplacingView && previousJoinWith && previousJoinWithColumn) {
            const joinWithSelectedColumns = updatedSelectedColumns[previousJoinWith]?.selectedColumns || [];
            updatedSelectedColumns[previousJoinWith] = {
              ...updatedSelectedColumns[previousJoinWith],
              selectedColumns: joinWithSelectedColumns.filter((col) => col !== previousJoinWithColumn),
            };
          }
        }
      }

      joinReferences[datasetId]?.forEach(({ datasetId: referencingDatasetId, joinWithColumn }) => {
        const referencingIndex = tableOrder.indexOf(referencingDatasetId);
        if (referencingIndex !== -1 && (referencingIndex <= idx || idx === -1)) {
          updatedSelectedColumns[referencingDatasetId] = {
            ...updatedSelectedColumns[referencingDatasetId],
            joinWith: "",
            joinWithColumn: null,
            joinColumn:
              datasetColumns[referencingDatasetId]?.filter(
                (col) =>
                  !["FX_UNIQUE_ID", "CRUXY_ID", "SCORE_", "WASALREADYMERGEDFROMDATASETS"].some((exclude) =>
                    col.includes(exclude)
                  )
              )[0] || null,
          };
          hasChanges = true;

          if (isReplacingView && joinWithColumn) {
            const currentSelectedColumns = updatedSelectedColumns[datasetId]?.selectedColumns || [];
            updatedSelectedColumns[datasetId] = {
              ...updatedSelectedColumns[datasetId],
              selectedColumns: currentSelectedColumns.filter((col) => col !== joinWithColumn),
            };
          }
        }
      });
    });

    if (hasChanges) {
      enqueueSnackbar("Some join selections were reset due to table order changes.", { variant: "warning" });
      setSelectedColumns(updatedSelectedColumns);
    }
  }, [tableOrder, datasetColumns, setSelectedColumns, selectedColumns, isReplacingView]);

  useEffect(() => {
    openedDatasetIds?.forEach((datasetId) => {
      if (!selectedColumns[datasetId]) {
        setSelectedColumns((prev) => ({
          ...prev,
          [datasetId]: {
            selectedColumns: [],
            joinColumn: null,
            joinWith: undefined,
            joinWithColumn: null,
          },
        }));
      }
    });
  }, [openedDatasetIds, selectedColumns, setSelectedColumns]);

  const joinWithColumnsMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    datasets.forEach((dataset) => {
      map[dataset.id] = Object.values(selectedColumns)
        .filter((config) => config.joinWith === dataset.id && config.joinWithColumn)
        .map((config) => config.joinWithColumn) as string[];
    });
    return map;
  }, [selectedColumns, datasets]);

  return (
    <MDBox
      sx={{
        width: "100%",
        marginBottom: "1rem",
      }}
    >
      <MDBox
        sx={{
          width: "100%",
          background: colors.background.default,
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)",
          borderRadius: pxToRem(12),
          padding: "1rem",
          border: `1px solid ${colors.grey[300]}`,
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <MDTypography fontWeight="medium" sx={{ color: colors.text.main, fontSize: "1.1rem" }}>
          Join Datasets
        </MDTypography>
        <Divider
          sx={{
            margin: 0,
            marginBottom: 0,
            borderColor: colors.grey[500],
            borderWidth: "1px",
          }}
        />
        <SearchDatasetCards onSearch={(query) => setSearchQuery(query)} />
        <MDTypography variant="subtitle2" sx={{ color: colors.text.main, fontSize: "0.85rem", mt: 1 }}>
          Select datasets below to join.
        </MDTypography>
        <MDBox
          sx={{
            padding: "0.5rem",
            display: "flex",
            gap: "1rem",
            overflowX: "auto",
            whiteSpace: "nowrap",
            paddingBottom: "1rem",
            width: "100%",
            "&::-webkit-scrollbar": {
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: colors.grey[400],
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: (theme) => theme.palette.primary.main,
              borderRadius: "10px",
              cursor: "grab",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: (theme) => theme.palette.primary.dark,
              cursor: "grabbing",
            },
            "&::-webkit-scrollbar-thumb:active": {
              cursor: "grabbing",
            },
            userSelect: "none",
          }}
        >
          {filteredDatasets.length > 0 ? (
            filteredDatasets.map((dataset, index) => (
              <MDBox
                key={dataset.id}
                sx={{
                  width: pxToRem(150),
                  textAlign: "center",
                  flex: "0 0 auto",
                }}
              >
                <MDBox
                  sx={{
                    height: pxToRem(75),
                    width: pxToRem(150),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: pxToRem(12),
                    background: openedDatasetIds.includes(dataset.id)
                      ? "linear-gradient(135deg, #0d00ff 0%, #b3c6ff 100%)"
                      : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(245, 245, 245, 0.7) 100%)",
                    backdropFilter: "blur(8px)",
                    boxShadow: openedDatasetIds.includes(dataset.id)
                      ? "0 8px 24px rgba(13, 0, 255, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2)"
                      : "0 6px 20px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.3)",
                    border: openedDatasetIds.includes(dataset.id)
                      ? "1px solid rgba(255, 255, 255, 0.5)"
                      : "1px solid rgba(0, 0, 0, 0.1)",
                    cursor: "pointer",
                    padding: pxToRem(16),
                    textAlign: "center",
                    opacity: 1,
                    transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out, background 0.3s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.05) translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.15)",
                      background: openedDatasetIds.includes(dataset.id)
                        ? "linear-gradient(135deg, #3b33ff 0%, #b3c6ff 100%)"
                        : "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(250, 250, 250, 0.9) 100%)",
                    },
                    "&:active": {
                      transform: "scale(0.98)",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                    },
                  }}
                  onClick={() => handleCardClick(dataset.id)}
                >
                  <Tooltip title={dataset.name} arrow>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      color={openedDatasetIds.includes(dataset.id) ? "white" : "secondary.main"}
                      sx={{
                        fontSize: pxToRem(14),
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        textShadow: openedDatasetIds.includes(dataset.id) ? "0 2px 4px rgba(0, 0, 0, 0.3)" : "none",
                      }}
                    >
                      {dataset.name.length > 12 ? `${dataset.name.slice(0, 10)}...` : dataset.name}
                    </MDTypography>
                  </Tooltip>
                </MDBox>
                {openedDatasetIds.includes(dataset.id) && (
                  <MDBox sx={{ marginTop: "0.5rem", textAlign: "left" }}>
                    {index !== 0 ? (
                      <StyledJoinCard>
                        <MDTypography
                          variant="h6"
                          fontWeight="medium"
                          mb={1}
                          sx={{ color: colors.text.main, fontSize: "1.1rem" }}
                        >
                          Join Settings
                        </MDTypography>
                        <hr style={{ backgroundColor: colors.grey[900], marginBottom: "0.5rem" }} />
                        <MDTypography variant="subtitle2" sx={{ fontSize: "0.8rem", mb: 0.5 }}>
                          Dataset to Join With:
                        </MDTypography>
                        <StyledSelect
                          value={selectedColumns[dataset.id]?.joinWith || ""}
                          onChange={(e) => handleJoinWithChange(dataset.id, e.target.value as string)}
                          size="small"
                          fullWidth
                          displayEmpty
                          sx={{ fontSize: "0.65rem" }}
                        >
                          <MenuItem value="">None</MenuItem>
                          {filteredDatasets
                            .filter(
                              (d) =>
                                tableOrder.indexOf(d.id) < tableOrder.indexOf(dataset.id) &&
                                openedDatasetIds.includes(d.id)
                            )
                            .map((d) => (
                              <MenuItem key={d.id} value={d.id}>
                                <Tooltip title={d.name} arrow>
                                  <span>{d.name.length > 12 ? `${d.name.slice(0, 10)}...` : d.name}</span>
                                </Tooltip>
                              </MenuItem>
                            ))}
                        </StyledSelect>
                        {selectedColumns[dataset.id]?.joinWith && (
                          <>
                            <MDTypography
                              variant="subtitle2"
                              sx={{ fontSize: "0.8rem", mt: 1, mb: 0.5, color: colors.text.main }}
                            >
                              Join by:
                            </MDTypography>
                            <StyledSelect
                              value={selectedColumns[dataset.id]?.joinWithColumn || ""}
                              onChange={(e) =>
                                handleJoinWithColumnChange(dataset.id, (e.target.value as string) || null)
                              }
                              size="small"
                              fullWidth
                              displayEmpty
                            >
                              <MenuItem value="">None</MenuItem>
                              {(datasetColumns[selectedColumns[dataset.id].joinWith] || [])
                                .filter(
                                  (column) =>
                                    column !== "FX_UNIQUE_ID" &&
                                    column !== "CRUXY_ID" &&
                                    !column.includes("SCORE_") &&
                                    column !== "WASALREADYMERGEDFROMDATASETS"
                                )
                                .map((column) => (
                                  <MenuItem key={column} value={column}>
                                    <Tooltip title={column} arrow>
                                      <span>{column.length > 12 ? `${column.slice(0, 10)}...` : column}</span>
                                    </Tooltip>
                                  </MenuItem>
                                ))}
                            </StyledSelect>
                          </>
                        )}
                      </StyledJoinCard>
                    ) : null}
                    <MergerTableColumns
                      columns={datasetColumns[dataset.id] || []}
                      selectedDatasetId={dataset.id}
                      selectedColumns={selectedColumns[dataset.id]?.selectedColumns || []}
                      joinColumn={selectedColumns[dataset.id]?.joinColumn || null}
                      handleColumnSelect={handleColumnSelect}
                      isLoading={isLoading}
                      tableOrder={tableOrder}
                      isReplacingView={isReplacingView}
                      joinWithColumn={joinWithColumnsMap[dataset.id] || []}
                    />
                  </MDBox>
                )}
              </MDBox>
            ))
          ) : (
            <MDBox sx={{ width: "100%", textAlign: "center", py: 2 }}>
              <MDTypography variant="body1" color="text.secondary">
                No datasets found
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    </MDBox>
  );
};

const StyledJoinCard = styled(MDBox)`
  width: ${pxToRem(150)};
  min-width: ${pxToRem(150)};
  box-sizing: border-box;
  flex-shrink: 0;
  background: ${colors.background.default};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid ${colors.grey[300]};
  margin-bottom: 1rem;
`;

const StyledSelect = styled(Select)`
  background: ${colors.secondary.main};
  font-size: 0.7rem;
  border-radius: 4px;
  transition: all 0.2s ease-in-out;
  margin-bottom: 0.5rem;

  &:hover {
    background: ${colors.secondary.focus};
    transform: scale(1.02);
  }

  & .MuiSelect-select {
    padding: 8px 14px;
  }

  & .MuiOutlinedInput-notchedOutline {
    border-color: ${colors.grey[300]};
  }

  &:nth-of-type(2) {
    background: ${colors.primary.main};
    &:hover {
      background: ${colors.primary.focus};
    }
  }
`;

export default CardsSection;
